import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Link, Spinner, Text, useToast } from '@chakra-ui/react';
import axios from 'axios';
import { Action, ItemType, Page } from '@/../../server/types/loggingTypes';
import Button from '@/components/lib/Button';
import Input from '@/components/lib/Input';
import { LoginMethod } from '@/routes/signup';
import { setRefreshToken } from '@/utils/auth';
import { getStatsigClient } from '../utils/statsig';
import OTPInput, { OTPInputRef } from './PhoneVerification/OTPInput';
import ResendOTP, { OTPType } from './PhoneVerification/ResendOTP';

/* TODO: Think about how to consolidate the email and phone verification components.
  - Resend is the same for both
  - OTPInput is the same for both
  - Link to switch between email and phone is the same for both
  - Main input field is the same for both, but field validation and placeholder text are different
*/

enum EmailVerificationStage {
  EnterEmail,
  VerifyEmail,
}

interface LoginWithEmailProps {
  onboardingRewardUniqueId?: string;
  setLoginMethod: Dispatch<SetStateAction<LoginMethod>>;
  enterFrom?: string;
  otpEmailAddress?: string;
}

const LoginWithEmail = ({
  onboardingRewardUniqueId,
  setLoginMethod,
  enterFrom,
  otpEmailAddress,
}: LoginWithEmailProps) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const [stage, setStage] = useState<EmailVerificationStage>(EmailVerificationStage.EnterEmail);
  const otpInputRef = useRef<OTPInputRef>(null);

  useEffect(() => {
    if (enterFrom === 'otp_email') {
      setStage(EmailVerificationStage.VerifyEmail);
    }

    if (otpEmailAddress) {
      setEmail(otpEmailAddress);
    }
  }, [enterFrom, otpEmailAddress]);

  const handleSendOtp = useCallback(
    (isResend = false) =>
      async () => {
        getStatsigClient().logEvent(Page.LANDING_PAGE, Action.CLICK, {
          item_name: isResend ? 'resend_otp' : 'send_email_otp',
          item_type: ItemType.BUTTON,
        });
        if (!email) {
          toast({
            title: 'Please enter an email address.',
            status: 'warning',
            duration: 3000,
          });
          return;
        }

        try {
          await axios.post('/api/auth/sendEmailVerificationCode', {
            email,
          });
          setStage(EmailVerificationStage.VerifyEmail);
          otpInputRef.current?.reset();
        } catch (error: any) {
          console.error(error);
          getStatsigClient().logEvent('signing_in_from_landing_page_error', error);
        }
      },
    [email, onboardingRewardUniqueId],
  );

  const handleKeyDown = useCallback(
    async (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleSendOtp()();
      }
    },
    [handleSendOtp],
  );

  const handleEmailChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!email) {
        getStatsigClient().logEvent(Page.LANDING_PAGE, Action.TYPING, {
          item_name: 'email',
        });
      }
      setEmail(event.target.value);
    },
    [email],
  );

  const handleOtpComplete = useCallback(
    async (otp: string) => {
      setIsLoading(true);
      getStatsigClient().logEvent('web_verify_email_code');
      try {
        let onboardingRewardUserId = undefined;
        if (onboardingRewardUniqueId) {
          const userRes = await axios.post('/api/getAskeeUser', {
            uniqId: onboardingRewardUniqueId,
          });
          if (userRes.data.user) {
            onboardingRewardUserId = userRes.data.user.userId;
          }
        }

        const res = await axios.post('/api/auth/verifyEmailCode', {
          email,
          code: otp,
          onboardingRewardUserId,
        });
        getStatsigClient().logEvent('web_verify_email_code_success');

        const { sessionToken, refreshToken } = res.data;
        localStorage.setItem('sessionToken', sessionToken);

        setRefreshToken(refreshToken);

        window.location.href = '/';
      } catch (error: any) {
        console.error(error);
        getStatsigClient().logEvent('web_verify_email_code_error', error);

        if (error.response.data.error === 'Invalid code') {
          toast({
            title: 'Invalid Code. Please check your code and try again.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top',
          });
        } else {
          toast({
            title: 'Something went wrong. Please try again.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top',
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [email],
  );

  const handleSwitchToPhone = useCallback(() => {
    getStatsigClient().logEvent(Page.LANDING_PAGE, Action.CLICK, {
      item_name: 'continue_with_phone',
      item_type: ItemType.BUTTON,
    });
    setLoginMethod(LoginMethod.PHONE);
  }, [setLoginMethod]);

  const handleBackToEmailInput = useCallback(() => {
    getStatsigClient().logEvent(Page.LANDING_PAGE, Action.CLICK, {
      item_name: 'back_to_email_input',
      item_type: ItemType.BUTTON,
    });
    setStage(EmailVerificationStage.EnterEmail);
  }, [setStage]);

  return (
    <>
      {stage === EmailVerificationStage.EnterEmail && (
        <div className="w-full max-w-[334px] mt-8">
          <Input
            type="email"
            autoComplete="email"
            placeholder="Enter your email to log in or sign up"
            value={email}
            onChange={handleEmailChange}
            onKeyDown={handleKeyDown}
            size="lg"
          />

          <div className="mt-8">
            <Link color="#ff0050" onClick={handleSwitchToPhone}>
              Continue with phone number
            </Link>
          </div>

          <Button className="mt-14" size="lg" isLoading={isLoading} onClick={handleSendOtp()}>
            Continue
          </Button>
        </div>
      )}

      {stage === EmailVerificationStage.VerifyEmail && (
        <>
          <div className="mt-8" />
          <Text color="white" fontSize="24px" fontWeight="bold">
            Enter the code we sent to
          </Text>
          <Text color="white" fontSize="24px" fontWeight="bold" contentEditable={false}>
            {email}
          </Text>
          <div className="mt-4" />
          <OTPInput ref={otpInputRef} onComplete={handleOtpComplete} />
          <div className="mt-2" />
          <ResendOTP type={OTPType.EMAIL} onResend={handleSendOtp(true)} />
          <div className="mt-2" />
          <Link color="white" onClick={handleBackToEmailInput} textDecoration="underline">
            or Use a different email address
          </Link>
          {isLoading && (
            <Box
              position="absolute"
              top="0"
              left="0"
              width="100%"
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="rgba(255, 255, 255, 0.6)" // Semi-transparent overlay
              zIndex="overlay"
            >
              <Spinner thickness="4px" speed="0.75s" emptyColor="#1c1c1c" color="#FF0050" size="lg" />
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default LoginWithEmail;
